import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { API_BASE_URL } from '../http/api-base-url.token';
import { joinUrl } from '../http/url.util';
import { AUTH_LOGIN_ROUTE } from './auth-login-route.token';
import { AuthIdentity, AuthSessionEngine, AuthTokens } from './auth-engine';

export interface CandidateLoginResult {
  accessToken: string;
  refreshToken: string;
  candidateUserId: string;
  candidateId: string | null;
}

export interface CandidateRegisterResult {
  candidateUserId: string;
  candidateId: string | null;
}

/**
 * The CANDIDATE auth scheme, sharing AuthSessionEngine with the company AuthService but pointed
 * at the candidate surface: `candidate/*` endpoints, its own bearer scheme (audience `candidate`
 * server-side), and its own storage prefix so the two schemes can never cross-read each other's
 * tokens. The candidate login/register responses are decoy-shaped (an existing account returns
 * plausible ids exactly as a fresh registration would), so nothing here branches on existence.
 */
@Injectable({ providedIn: 'root' })
export class CandidateAuthService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);

  // Field-initializer order matters: the engine is built (and restores localStorage) before the
  // signals below are derived from it.
  readonly #engine = new AuthSessionEngine(
    {
      storagePrefix: 'rekruto_candidate',
      refreshPath: 'candidate/refresh',
      logoutPath: 'candidate/logout',
      identityKeys: ['candidateUserId', 'candidateId'],
      unauthenticatedPaths: ['candidate/login', 'candidate/register', 'candidate/refresh', 'candidate/logout'],
    },
    this.http,
    this.apiBaseUrl,
    inject(Router),
    inject(AUTH_LOGIN_ROUTE),
  );

  readonly token = this.#engine.accessToken;
  readonly isAuthenticated = this.#engine.isAuthenticated;
  readonly candidateUserId = this.#engine.identityValue('candidateUserId');
  readonly candidateId = this.#engine.identityValue('candidateId');
  /** Endpoint paths whose own 401 must reach the caller, never trigger refresh or sign-out. */
  readonly unauthenticatedPaths = this.#engine.unauthenticatedPaths();

  login(email: string, password: string): Observable<CandidateLoginResult> {
    return this.http
      .post<CandidateLoginResult>(joinUrl(this.apiBaseUrl, 'candidate/login'), { email, password })
      .pipe(
        tap(result =>
          this.#engine.adoptSession(
            { accessToken: result.accessToken, refreshToken: result.refreshToken },
            {
              candidateUserId: result.candidateUserId,
              candidateId: result.candidateId,
            } satisfies AuthIdentity,
          ),
        ),
      );
  }

  register(email: string, password: string): Observable<CandidateRegisterResult> {
    return this.http.post<CandidateRegisterResult>(joinUrl(this.apiBaseUrl, 'candidate/register'), {
      email,
      password,
    });
  }

  /**
   * Exchanges the stored refresh grant for a fresh pair, rotating storage in place. One in-flight
   * refresh per scheme: concurrent callers share the same request.
   */
  refresh(): Observable<AuthTokens> {
    return this.#engine.refresh();
  }

  logout(): void {
    this.#engine.logout();
  }
}